import { Ref, ref } from '@vue/composition-api';
import {
	fetchCampaignInviteeCounts,
	fetchInviteeInvites,
	updateInviteeStatus,
	pauseOrResumeStatuses,
} from '@front-app/services/GroupInvitationCampaignApi/InvitationCampaignInviteesApi';
import {
	InvitationCampaignInvitee,
	InviteeStatus,
	InviteeCounts,
	InviteeCountsResponse,
	UpdateInviteeStatusesRequest,
} from '@front-app/services/GroupInvitationCampaignApi/model';
import { CampaignInviteeSearchArgs, useThrottledCampaignInviteesSearch } from '@front-app/hooks/ThrottledApi/campaignInviteeSearch';
import { useCache } from '../util/cache';

export interface InvitationCampaignInviteeStore {
	fetchInviteeCounts(groupId: string, campaignId: string): Promise<void>;
	fetchInviteeInviteDates(groupId: string, campaignId: string, inviteeId: string): Promise<Date[]>;
	inviteeCounts: Ref<InviteeCounts>;
	fetchInviteesPage(...args: CampaignInviteeSearchArgs): Promise<string[]>;
	inviteeById(inviteeId: string): InvitationCampaignInvitee;
	changeInviteeStatuses(
		groupId: string,
		campaignId: string,
		payload: UpdateInviteeStatusesRequest,
	): Promise<void>;
	pauseOrResumeInviteeStatuses(
		groupId: string,
		campaignId: string,
		status: InviteeStatus,
	): Promise<void>;
	getCountForStatus(status: InviteeStatus): number;
}

export const getInvitationCampaignInviteesStore = (): InvitationCampaignInviteeStore => {
	const inviteesCache = useCache({}, getDefaultInvitee);
	const inviteeCounts = ref({}) as Ref<InviteeCounts>;

	const fetchInviteeCounts = async (groupId: string, campaignId: string) => {
		const countsFromApi = await fetchCampaignInviteeCounts(groupId, campaignId);
		const counts = {} as InviteeCounts;
		let total = 0;
		Object.keys(countsFromApi).forEach((count: string) => {
			const currentCount = countsFromApi[count as keyof InviteeCountsResponse];
			counts[count as keyof InviteeCounts] = currentCount;
			if (count !== 'removed') {
				total += currentCount;
			}
		});
		counts.all = total;
		inviteeCounts.value = counts;
	};

	const fetchInviteeInviteDates = async (groupId: string, campaignId: string, inviteeId: string) => {
		return await fetchInviteeInvites(groupId, campaignId, inviteeId);
	}

	const getCountForStatus = (status: InviteeStatus): number => {
		switch (status) {
		case InviteeStatus.ACTIVE:
			return inviteeCounts.value.active;
		case InviteeStatus.JOINED:
			return inviteeCounts.value.joined;
		case InviteeStatus.DECLINED:
			return inviteeCounts.value.declined;
		case InviteeStatus.PAUSED:
			return inviteeCounts.value.paused;
		default:
			// eslint-disable-next-line no-case-declarations
			const exhaustiveCheck: never = status as never;
			throw new Error(`Non-valid switch condition met: ${exhaustiveCheck}`);
		}
	};

	const changeInviteeStatuses = async (groupId: string, campaignId: string, payload: UpdateInviteeStatusesRequest) => {
		const updatedInvitees = await updateInviteeStatus(groupId, campaignId, {
			inviteeIds: payload.inviteeIds,
			status: payload.status,
		});
		updatedInvitees.forEach((updatedInvitee) => inviteesCache.update(updatedInvitee.inviteeId, updatedInvitee));
	};

	const pauseOrResumeInviteeStatuses = async(groupId: string, campaignId: string, status: InviteeStatus) => {
		await pauseOrResumeStatuses(groupId, campaignId, status);
	};

	const { search } = useThrottledCampaignInviteesSearch();
	const fetchInviteesPage = async (...args: CampaignInviteeSearchArgs): Promise<string[]> => {
		const results = await search(...args);
		// Set results in the inviteeStore
		results.forEach((invitee) => inviteesCache.set(invitee.inviteeId, invitee));
		return results.map(({ inviteeId }) => inviteeId);
	};

	return {
		inviteeById: inviteesCache.get,
		fetchInviteesPage,
		fetchInviteeCounts,
		fetchInviteeInviteDates,
		inviteeCounts,
		changeInviteeStatuses,
		pauseOrResumeInviteeStatuses,
		getCountForStatus,
	};
};

function getDefaultInvitee(id: string): InvitationCampaignInvitee {
	return {
		inviteeId: id,
		userId: null,
		email: '',
		status: InviteeStatus.ACTIVE,
		numTimesNotified: 0,
		invitedDateTime: '',
		firstNotificationDateTime: null,
		lastNotificationDateTime: '',
	};
}
